import { useMutation, useQuery } from "@tanstack/react-query";
import { useState, useCallback, useRef } from "react";
import { CelebrationType } from "./metricsApi";
import {
  TextType,
  generatePlanText,
  postOutreachSms,
  postChat,
  GetChatsResult,
  getChats,
  extractClassSchedule,
  GetHistoricalChatResult,
  getHistoricalChat,
  deleteChat,
  renameChat,
} from "./aiApi";
import { PlanForm } from "src/pages/Plans/PlanDrawer/PlanDrawer.types";

export function usePostOutreachSms() {
  return useMutation({
    mutationFn: ({
      memberName,
      gymName,
      numberOfYears,
      celebrationType,
    }: {
      memberName: string;
      gymName: string;
      numberOfYears?: number;
      celebrationType: CelebrationType;
    }) =>
      postOutreachSms({ memberName, gymName, numberOfYears, celebrationType }),
  });
}

export function useGeneratePlanText() {
  return useMutation({
    mutationFn: ({ textType, plan }: { textType: TextType; plan: PlanForm }) =>
      generatePlanText({ textType, plan }),
  });
}

export function useGetChats() {
  return useQuery<GetChatsResult>({
    queryFn: () => getChats(),
  });
}

export function useGetHistoricalChat(
  chatUuid: string,
  options?: { skip?: boolean },
) {
  return useQuery<GetHistoricalChatResult>({
    queryFn: () => getHistoricalChat(chatUuid),
    queryKey: ["historicalChat", chatUuid],
    enabled: !options?.skip,
  });
}

export function useDeleteChat() {
  return useMutation({
    mutationFn: ({ chatUuid }: { chatUuid: string }) => deleteChat(chatUuid),
  });
}

export function useRenameChat() {
  return useMutation({
    mutationFn: ({
      chatUuid,
      newTitle,
    }: {
      chatUuid: string;
      newTitle: string;
    }) => renameChat(chatUuid, newTitle),
  });
}

export function useChat() {
  const [isLoading, setIsLoading] = useState(false);
  const [streamedText, setStreamedText] = useState<string>();
  const [newChatUuid, setNewChatUuid] = useState<string>();

  const accumulatedRawTextRef = useRef("");
  const accumulatedParsedMessageRef = useRef("");

  const sendMessage = useCallback(
    async ({
      message,
      chatUuid,
      interruptionStatus,
    }: {
      message: string;
      chatUuid?: string;
      interruptionStatus?: "approved" | "rejected";
    }) => {
      setIsLoading(true);
      accumulatedRawTextRef.current = "";
      accumulatedParsedMessageRef.current = "";
      setStreamedText("");

      const response = await postChat({
        message,
        chatUuid,
        interruptionStatus,
      });

      const reader = response.body?.getReader();
      const decoder = new TextDecoder("utf-8");
      let done = false;

      // Tracks the length of the message part last extracted to append only new content.
      let lastExtractedMessagePartLength = 0;

      try {
        while (!done && done !== undefined) {
          const { value, done: readerDone } = (await reader?.read()) || {};
          done = readerDone;

          if (value) {
            const chunkValue = decoder.decode(value, { stream: true });

            accumulatedRawTextRef.current += chunkValue;
            const messageMatch = accumulatedRawTextRef.current.match(
              /"message"\s*:\s*"((?:[^"\\]|\\.)*)"?/,
            );

            let currentMessageExtractedContent = "";
            if (messageMatch && messageMatch[1] !== undefined) {
              currentMessageExtractedContent = messageMatch[1];
              try {
                currentMessageExtractedContent = JSON.parse(
                  `"${currentMessageExtractedContent}"`,
                );
              } catch (e) {
                currentMessageExtractedContent = currentMessageExtractedContent
                  .replace(/\\n/g, "\n") // Unescape newline
                  .replace(/\\"/g, '"'); // Unescape double quotes
                console.warn(
                  "Failed to fully unescape streamed message part (falling back to simple replace):",
                  e,
                  currentMessageExtractedContent,
                );
              }
            }

            if (
              currentMessageExtractedContent.length >
              lastExtractedMessagePartLength
            ) {
              accumulatedParsedMessageRef.current +=
                currentMessageExtractedContent.substring(
                  lastExtractedMessagePartLength,
                );
              lastExtractedMessagePartLength =
                currentMessageExtractedContent.length;
            }
          }
          setStreamedText(accumulatedParsedMessageRef.current);
        }
      } finally {
        setIsLoading(false);
      }
      try {
        const finalParsedResponse = JSON.parse(accumulatedRawTextRef.current);
        setNewChatUuid(finalParsedResponse.chatUuid);
        return finalParsedResponse;
      } catch (e) {
        throw new Error("Failed to parse final response", { cause: e });
      }
    },
    [],
  );

  return {
    sendMessage,
    isLoading,
    streamedText,
    setStreamedText,
    newChatUuid,
  };
}

export function useExtractClassSchedule() {
  return useMutation({
    mutationFn: ({
      type,
      content,
    }: {
      type: "text" | "image";
      content: string;
    }) => extractClassSchedule({ type, content }),
  });
}
